import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS, QUERY_PARAMS } from "@salesboard/contracts";
import { useFilterSet, type FilterDef, type FilterDefs, type FilterSet } from "./useFilterSet";

// Re-exported so the many screens that import their filter vocabulary from here keep
// working; the definitions themselves now live with the filter seam that both the board
// and the list share.
export type { FilterDef, FilterDefs, FilterSet };

export type SortState = { field: string; direction: "asc" | "desc" };

export type ServerTable = FilterSet & {
  /** Ready to hand to a data hook; matches the API's query vocabulary exactly. */
  queryParams: Record<string, string | number | undefined>;
  /** Stable React Query key derived from the same params. */
  queryKey: readonly unknown[];
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  setPerPage: (perPage: number) => void;
  sort: SortState;
  toggleSort: (field: string) => void;
};

const STORAGE_PREFIX = "table-perPage-";

function readStoredPerPage(tableId: string): number {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + tableId);
    const parsed = stored ? Number.parseInt(stored, 10) : Number.NaN;
    return (PER_PAGE_OPTIONS as readonly number[]).includes(parsed) ? parsed : DEFAULT_PER_PAGE;
  } catch {
    return DEFAULT_PER_PAGE;
  }
}

/**
 * The seam between a **paginated** list screen, the URL, the API and the query cache.
 *
 * Filtering is `useFilterSet`, shared with the board — this adds the page, the sort and
 * the rows-per-page preference a table needs and a board does not. Parameter names come
 * from `packages/contracts`, so a rename fails typecheck on both sides rather than
 * silently producing a URL the API ignores.
 *
 * Pagination, sorting and filtering are **server-side** — this returns parameters, not
 * a filtered array. Rows-per-page is the one piece of state kept in localStorage
 * instead of the URL, because it is a display preference rather than part of the view.
 */
export function useServerTable(
  tableId: string,
  defs: FilterDefs,
  defaultSort: SortState = { field: "name", direction: "asc" },
): ServerTable {
  const [searchParams, setSearchParams] = useSearchParams();
  const [perPage, setPerPageState] = useState(() => readStoredPerPage(tableId));

  // See useFilterSet: carried through every replace so a modal's background survives
  // paging and sorting, and held in a ref so the memoised setters do not churn.
  const location = useLocation();
  const stateRef = useRef(location.state);
  stateRef.current = location.state;

  const namespaced = useCallback((key: string) => `${tableId}:${key}`, [tableId]);

  // Changing a filter invalidates the current page: page 4 of the old result set is
  // meaningless against the new one, and often empty. Handed to the filter seam so it
  // happens inside the same URL mutation rather than as a second write.
  const dropPage = useCallback((params: URLSearchParams) => params.delete(QUERY_PARAMS.page), []);
  const filterSet = useFilterSet(tableId, defs, { onChange: dropPage });

  const page = Math.max(1, Number.parseInt(searchParams.get(QUERY_PARAMS.page) ?? "1", 10) || 1);

  const sort = useMemo<SortState>(() => {
    const raw = searchParams.get(namespaced(QUERY_PARAMS.sort));
    if (!raw) return defaultSort;
    const [field, direction] = raw.split(":");
    if (!field || (direction !== "asc" && direction !== "desc")) return defaultSort;
    return { field, direction };
  }, [searchParams, namespaced, defaultSort]);

  const mutate = useCallback(
    (fn: (params: URLSearchParams) => void) => {
      // Live read, per useFilterSet's reasoning: several setters may fire in one
      // handler and each must see the previous one's result.
      const params = new URLSearchParams(window.location.search);
      fn(params);
      // See useFilterSet: a state-less replace erases the modal background.
      setSearchParams(params, { replace: true, state: stateRef.current });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (next: number) => {
      mutate((params) => {
        if (next <= 1) params.delete(QUERY_PARAMS.page);
        else params.set(QUERY_PARAMS.page, String(next));
      });
    },
    [mutate],
  );

  const setPerPage = useCallback(
    (next: number) => {
      setPerPageState(next);
      try {
        localStorage.setItem(STORAGE_PREFIX + tableId, String(next));
      } catch {
        // Private browsing; the preference simply does not persist.
      }
      setPage(1);
    },
    [tableId, setPage],
  );

  const toggleSort = useCallback(
    (field: string) => {
      const next: SortState =
        sort.field === field
          ? { field, direction: sort.direction === "asc" ? "desc" : "asc" }
          : { field, direction: "asc" };

      mutate((params) => {
        const isDefault = next.field === defaultSort.field && next.direction === defaultSort.direction;
        if (isDefault) params.delete(namespaced(QUERY_PARAMS.sort));
        else params.set(namespaced(QUERY_PARAMS.sort), `${next.field}:${next.direction}`);
        params.delete(QUERY_PARAMS.page);
      });
    },
    [sort, mutate, namespaced, defaultSort],
  );

  const { apiParams, activeFilterCount } = filterSet;

  const queryParams = useMemo(
    () => ({
      [QUERY_PARAMS.page]: page,
      [QUERY_PARAMS.perPage]: perPage,
      [QUERY_PARAMS.sort]: `${sort.field}:${sort.direction}`,
      ...apiParams,
    }),
    [page, perPage, sort, apiParams],
  );

  // A filter change can leave you past the end of the new result set. Clamping needs
  // the total, which only the caller has, so the caller passes it to PaginationControl;
  // here we only reset when the filter set itself changes.
  useEffect(() => {
    if (page > 1 && activeFilterCount === 0 && searchParams.get(QUERY_PARAMS.page) === null) setPage(1);
  }, [page, activeFilterCount, searchParams, setPage]);

  return {
    ...filterSet,
    queryParams,
    queryKey: [tableId, queryParams] as const,
    page,
    setPage,
    perPage,
    setPerPage,
    sort,
    toggleSort,
  };
}
